import React, { useState } from "react";
import Modal from "../generic/Modal";
import InputField from "../generic/InputField";
import AlertBanner from "../generic/AlertBanner";
import { MESSAGE_TYPES, Messages } from "../../constants/Messages";
import { register } from "../../services/AuthAPI";
import { validatePassword } from "../../utils/PasswordUtils";
import PasswordRequirements from "./PasswordRequirements";
import SubmitButton from "../generic/SubmitButton";

interface RegisterProps {
    onClose: () => void; // Function to close the modal
    onResponseData: (response: { success: boolean; message: string }) => void; // Callback to pass API response data
    onForgotPasswordClick: () => void; // Navigate to forgot password flow
    onLoginClick: () => void; // Navigate to login user flow
}

const Register: React.FC<RegisterProps> = ({
                                               onClose,
                                               onResponseData,
                                               onForgotPasswordClick,
                                               onLoginClick,
                                           }) => {
    const [ username, setUsername ] = useState<string>("");
    const [ email, setEmail ] = useState<string>("");
    const [ password, setPassword ] = useState<string>("");
    const [ confirmPassword, setConfirmPassword ] = useState<string>("");
    const [ validationError, setValidationError ] = useState<string>("");
    const [ responseData, setResponseData ] = useState<{ success: boolean; message: string }>({} as any);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username || !email || !password || !confirmPassword) {
            setValidationError(Messages.ERROR_ALL_FIELDS_REQUIRED);
            return;
        }

        // Validate password requirements
        const validationError = validatePassword(password);
        if (validationError) {
            setValidationError(validationError);
            return;
        }

        if (password !== confirmPassword) {
            setValidationError(Messages.ERROR_PASSWORD_NOT_MATCH);
            return;
        }

        try {
            const response = await register(username, email, password);

            // Pass the response data to the parent component
            onResponseData(response);

            // Handle validation errors only
            if (!response.user) {
                setValidationError(response.message);
            } else {
                setValidationError(""); // Clear validation errors on success
            }
            setResponseData(response);
        } catch (error: any) {
            setValidationError(`Registration Error: ${error.message}`);
        }
    };

    return (
        <Modal onClose={onClose}>
            <h2 className="text-lg font-semibold mb-2 text-center">Register</h2>

            {validationError.length > 0 && (
                <AlertBanner
                    message={validationError || ""}
                    message_type={MESSAGE_TYPES.ERROR}
                    onClose={() => setValidationError("")}
                />
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <InputField
                    type="text"
                    value={username}
                    onChange={(value) => setUsername(value as string)}
                    label="Username"
                    placeholder="Enter your username"
                    required
                    id="username"
                />
                <InputField
                    type="email"
                    value={email}
                    onChange={(value) => setEmail(value as string)}
                    label="Email"
                    placeholder="Enter your email"
                    required
                    id="email"
                />
                <InputField
                    type="password"
                    value={password}
                    onChange={(value) => setPassword(value as string)}
                    label="Password"
                    placeholder="Enter your password"
                    required
                    id="password"
                />
                <PasswordRequirements password={password}/>
                <InputField
                    type="password"
                    value={confirmPassword}
                    onChange={(value) => setConfirmPassword(value as string)}
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    required
                    id="confirm-password"
                />
                <SubmitButton>
                    Register
                </SubmitButton>

                <div className="mt-6 text-sm text-center">
                    <p>
                        Already have an account?{" "}
                        <a
                            onClick={onLoginClick}
                            className="text-blue-600 hover:text-blue-800 underline transition duration-200"
                        >
                            Log in
                        </a>
                    </p>
                    <p className="mt-2">
                        Trouble logging in?{" "}
                        <a
                            onClick={onForgotPasswordClick}
                            className="text-blue-600 hover:text-blue-800 underline transition duration-200"
                        >
                            Reset your password
                        </a>
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default Register;
